import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthContext'
import { LinkedText } from './LinkedText'

interface Props {
  ownerUserId: string | null | undefined
  value: string | null | undefined
  onSave: (notes: string | null) => Promise<void>
}

export function EditableNotes({ ownerUserId, value, onSave }: Props) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canEdit =
    Boolean(user) &&
    (user!.id === ownerUserId || user!.moderator || user!.admin)

  useEffect(() => {
    if (!editing) setDraft(value ?? '')
  }, [value, editing])

  if (!canEdit && !(value && value.trim())) return null

  async function handleSave() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const next = draft.trim() || null
      await onSave(next)
      setEditing(false)
    } catch (caught) {
      setError(
        caught instanceof Error && caught.message
          ? caught.message
          : t('notes.saveFailed'),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="editable-notes">
      <div className="editable-notes-header">
        <h3 className="editable-notes-title">{t('fields.notes')}</h3>
        {canEdit && !editing && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setDraft(value ?? '')
              setError(null)
              setEditing(true)
            }}
          >
            {value?.trim() ? t('notes.edit') : t('notes.add')}
          </button>
        )}
      </div>

      {editing ? (
        <div className="editable-notes-editor">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={4}
            maxLength={4000}
            disabled={busy}
            aria-label={t('fields.notes')}
          />
          {error && (
            <p className="field-error" role="alert">
              {error}
            </p>
          )}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={() => {
                setEditing(false)
                setDraft(value ?? '')
                setError(null)
              }}
            >
              {t('actions.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => void handleSave()}
            >
              {busy ? t('notes.saving') : t('notes.save')}
            </button>
          </div>
        </div>
      ) : value?.trim() ? (
        <p className="editable-notes-body">
          <LinkedText text={value} />
        </p>
      ) : canEdit ? (
        <p className="field-help">{t('notes.empty')}</p>
      ) : null}
    </div>
  )
}
